import React, { useState, useEffect, useMemo } from 'react';
import { auth, db } from '../services/firebase';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { Box, Typography, Card, CardContent, List, ListItem, ListItemText, ListItemAvatar, Avatar, CircularProgress, Chip, TextField } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import styles from '../styles/Dashboard.module.css';
import { format, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ControleAcesso = () => {
  const [prestadores, setPrestadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [condominioId, setCondominioId] = useState('');
  const [termoBusca, setTermoBusca] = useState('');

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setCondominioId(userDoc.data().condominio_id);
        }
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!condominioId) return;

    const q = query(collection(db, 'tb_prestadores_servico_morador'), where('condominio_id', '==', condominioId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPrestadores(list);
      setLoading(false);
    }, (error) => {
      console.error("Erro ao buscar prestadores:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [condominioId]);

  const prestadoresAutorizadosHoje = useMemo(() => {
    const hoje = new Date();
    // CORREÇÃO: O formato 'EEE' com o locale ptBR pode retornar o dia por extenso.
    // Para garantir a abreviação de 3 letras (Seg, Ter, etc.), usamos um mapeamento.
    const diasMap = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const diaSemanaHoje = diasMap[hoje.getDay()];

    return prestadores.filter(p => {
      if (p.tipo_acesso === 'recorrente') {
        return p.dias_semana?.includes(diaSemanaHoje);
      }
      if (p.tipo_acesso === 'pontual' && p.data_pontual) {
        return isToday(p.data_pontual.toDate());
      }
      return false;
    }).filter(p => 
      p.nome.toLowerCase().includes(termoBusca.toLowerCase())
    ).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [prestadores, termoBusca]);

  const verificarStatusHorario = (prestador) => {
    const agora = new Date();
    const { horario_inicio, horario_fim } = prestador;

    if (!horario_inicio || !horario_fim) {
      return { texto: 'Horário não definido', cor: 'default' };
    }

    const [startHour, startMinute] = horario_inicio.split(':').map(Number);
    const [endHour, endMinute] = horario_fim.split(':').map(Number);

    const startTime = new Date();
    startTime.setHours(startHour, startMinute, 0, 0);

    const endTime = new Date();
    endTime.setHours(endHour, endMinute, 0, 0);

    if (agora >= startTime && agora <= endTime) {
      return { texto: 'Autorizado Agora', cor: 'success' };
    }
    return { texto: 'Fora de Horário', cor: 'error' };
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>
          Controle de Acesso - Prestadores Autorizados para Hoje ({format(new Date(), 'dd/MM/yyyy')})
        </Typography>

        <Card className={styles.card}>
          <CardContent>
            <TextField
              fullWidth
              label="Buscar por nome do prestador"
              variant="outlined"
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              sx={{ mb: 2 }}
            />
            <List>
              {prestadoresAutorizadosHoje.length > 0 ? (
                prestadoresAutorizadosHoje.map((p) => (
                  <ListItem key={p.id} divider>
                    <ListItemAvatar>
                      <Avatar src={p.foto_url} sx={{ width: 56, height: 56, mr: 2 }}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                            {p.nome}
                          </Typography>
                          <Chip
                            label={verificarStatusHorario(p).texto}
                            color={verificarStatusHorario(p).cor}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={`Destino: Apto ${p.morador_apto} (${p.morador_nome}) | Horário: ${p.horario_inicio || 'N/A'} - ${p.horario_fim || 'N/A'}`}
                    />
                  </ListItem>
                ))
              ) : (
                <Typography sx={{ p: 2, textAlign: 'center' }}>Nenhum prestador de serviço autorizado para hoje.</Typography>
              )}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ControleAcesso;
